"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.useEvidence = void 0;
const react_1 = require("react");
const react_router_dom_1 = require("react-router-dom");
const lib_1 = require("use-debounce/lib");
const crud_source_hook_1 = require("../../utils/crud-source-hook");
const event_callback_hook_1 = require("../../utils/event-callback-hook");
const empty_component_1 = require("../../utils/empty-component");
const scrollable_source_hook_1 = require("../../utils/scrollable-source-hook");
const evidence_types_1 = require("./evidence-types");
const navigation_context_1 = require("../navigation/navigation-context");
const split_screen_types_1 = require("../../components/split-screen/split-screen-types");
const item_redirect_hook_1 = require("./hook/item-redirect-hook");
const hooked_source_hook_1 = require("./hook/hooked-source-hook");
const evidence_defaults_context_1 = require("../evidence-defaults/evidence-defaults-context");
function useEvidence({ tableProps, detailProps, apiProps, }) {
    var _a, _b, _c, _d, _e;
    item_redirect_hook_1.useEvidenceItemRedirect();
    const route = react_router_dom_1.useRouteMatch();
    const { id } = react_router_dom_1.useParams();
    const { search } = react_router_dom_1.useLocation();
    const params = new URLSearchParams(search);
    const { splitScreen: defaultSplitScreenMode } = react_1.useContext(evidence_defaults_context_1.EvidenceDefaultsContext);
    const { hookedSources, refreshAllHookedSources, resetAllHookedSources, } = hooked_source_hook_1.useHookedSources(apiProps.hookedSources);
    const tableSource = scrollable_source_hook_1.useScrollableSource({
        url: apiProps.url + '/list',
        listItems: apiProps.listItems,
    });
    const crudSource = crud_source_hook_1.useCrudSource({
        url: apiProps.url,
        getItem: apiProps.getItem,
        createItem: apiProps.createItem,
        updateItem: apiProps.updateItem,
        deleteItem: apiProps.deleteItem,
        refreshAllHookedSources,
        resetAllHookedSources,
    });
    const tableRef = react_1.useRef(null);
    const detailRef = react_1.useRef(null);
    const splitScreenRef = react_1.useRef(null);
    const { name: appName } = react_1.useContext(evidence_defaults_context_1.EvidenceDefaultsContext);
    const { stateAction, navigate } = react_1.useContext(navigation_context_1.NavigationContext);
    react_1.useEffect(() => {
        if ((stateAction === null || stateAction === void 0 ? void 0 : stateAction.action) === evidence_types_1.EvidenceStateAction.NEW_ITEM) {
            // needs to do in next frame after Detail component is rendered
            requestAnimationFrame(() => {
                var _a;
                (_a = detailRef.current) === null || _a === void 0 ? void 0 : _a.startNew(stateAction.data);
            });
        }
        if ((stateAction === null || stateAction === void 0 ? void 0 : stateAction.action) === evidence_types_1.EvidenceStateAction.SHOW_ITEM) {
            requestAnimationFrame(() => {
                var _a;
                (_a = tableRef.current) === null || _a === void 0 ? void 0 : _a.setActiveRow(stateAction.data);
            });
        }
    }, [stateAction]);
    react_1.useEffect(() => {
        var _a;
        if (!id) {
            (_a = tableRef.current) === null || _a === void 0 ? void 0 : _a.setActiveRow(null);
            handleActiveRowChange(null);
        }
    }, [id]);
    const handleRowClick = event_callback_hook_1.useEventCallback((id) => {
        var _a, _b;
        (_a = tableProps === null || tableProps === void 0 ? void 0 : tableProps.onRowClick) === null || _a === void 0 ? void 0 : _a.call(tableProps, id);
        if (id !== ((_b = tableRef.current) === null || _b === void 0 ? void 0 : _b.activeRow)) {
            const query = params.toString();
            navigate(route.path.replace(':id', id) + query, false, {
                action: evidence_types_1.EvidenceStateAction.SHOW_ITEM,
                data: id,
            });
        }
    });
    const handleActiveRowChange = event_callback_hook_1.useEventCallback((id) => {
        var _a, _b, _c, _d, _e, _f;
        (_a = detailRef.current) === null || _a === void 0 ? void 0 : _a.setActive(id);
        (_c = (_b = detailRef.current) === null || _b === void 0 ? void 0 : _b.formRef) === null || _c === void 0 ? void 0 : _c.resetValidation();
        const isFullscreenTable = (_d = splitScreenRef.current) === null || _d === void 0 ? void 0 : _d.isLeftOnFullscreen();
        if (isFullscreenTable) {
            (_e = splitScreenRef.current) === null || _e === void 0 ? void 0 : _e.handleMoveToMiddle();
        }
        (_f = tableProps === null || tableProps === void 0 ? void 0 : tableProps.onActiveChange) === null || _f === void 0 ? void 0 : _f.call(tableProps, id);
    });
    const [handleActiveRowChangeDebounced] = lib_1.useDebouncedCallback(handleActiveRowChange, 500);
    const handleDetailPersisted = event_callback_hook_1.useEventCallback((id) => {
        var _a, _b, _c;
        (_a = tableRef.current) === null || _a === void 0 ? void 0 : _a.refresh();
        (_b = tableRef.current) === null || _b === void 0 ? void 0 : _b.setActiveRow(id);
        if (!id) {
            navigate(route.path.replace(':id', ''), true);
        }
        else if (id !== ((_c = tableRef.current) === null || _c === void 0 ? void 0 : _c.activeRow)) {
            const query = params.toString();
            navigate(route.path.replace(':id', id) + query, false, {
                action: evidence_types_1.EvidenceStateAction.SHOW_ITEM,
                data: id,
            });
        }
    });
    const columns = (_a = tableProps === null || tableProps === void 0 ? void 0 : tableProps.columns) !== null && _a !== void 0 ? _a : [];
    const FieldsComponent = (_b = detailProps === null || detailProps === void 0 ? void 0 : detailProps.FieldsComponent) !== null && _b !== void 0 ? _b : empty_component_1.EmptyComponent;
    const defaultTitle = react_1.useRef(appName !== null && appName !== void 0 ? appName : document.title);
    const detailTitle = react_1.useMemo(() => {
        var _a, _b;
        let tabTitle = '';
        if ((_a = detailProps === null || detailProps === void 0 ? void 0 : detailProps.toolbarProps) === null || _a === void 0 ? void 0 : _a.title) {
            const title = detailProps.toolbarProps.title;
            tabTitle =
                typeof title === 'string'
                    ? title
                    : typeof title === 'function'
                        ? title(crudSource.data)
                        : '';
        }
        if ((_b = detailProps === null || detailProps === void 0 ? void 0 : detailProps.toolbarProps) === null || _b === void 0 ? void 0 : _b.subtitle) {
            const subtitle = detailProps.toolbarProps.subtitle;
            const subtitleText = typeof subtitle === 'string'
                ? subtitle
                : typeof subtitle === 'function'
                    ? subtitle(crudSource.data)
                    : '';
            tabTitle = subtitleText ? `${tabTitle} - ${subtitleText}` : tabTitle;
        }
        return tabTitle;
    }, [
        crudSource.data,
        (_c = detailProps === null || detailProps === void 0 ? void 0 : detailProps.toolbarProps) === null || _c === void 0 ? void 0 : _c.subtitle,
        (_d = detailProps === null || detailProps === void 0 ? void 0 : detailProps.toolbarProps) === null || _d === void 0 ? void 0 : _d.title,
    ]);
    const tableTitle = (_e = tableProps === null || tableProps === void 0 ? void 0 : tableProps.tableName) !== null && _e !== void 0 ? _e : document.title;
    return {
        columns,
        FieldsComponent,
        tableSource,
        crudSource,
        hookedSources,
        tableRef,
        splitScreenRef,
        detailRef,
        handleRowClick,
        handleActiveRowChange: handleActiveRowChangeDebounced,
        handleDetailPersisted,
        defaultTitle,
        detailTitle,
        tableTitle,
        defaultSplitScreenMode: 
        // combined Table (when id is not specified) - Detail (when id is specified) view
        defaultSplitScreenMode === split_screen_types_1.SplitScreenMode.LEFT && id
            ? split_screen_types_1.SplitScreenMode.RIGHT
            : defaultSplitScreenMode,
    };
}
exports.useEvidence = useEvidence;
//# sourceMappingURL=evidence-hook.js.map